import React, { useState, useEffect } from "react";
import { getData, deleteData } from "../../utils/awsService";
import { useNavigate } from "react-router-dom";
import "./AdminPanel.css";

const ContactFormPanel = () => {
  const [contactForms, setContactForms] = useState([]);
  const [filteredForms, setFilteredForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchContactForms = async () => {
      try {
        setLoading(true);
        setError(null);

        const responseData = await getData("/contact_form");

        // Extract contact form data
        let contactFormData = responseData.data["data"];

        // Transform data to extract specific fields and ensure timestamp exists
        const processedContactForms = contactFormData.map((contact) => ({
          id: contact.id || null,
          fullName: contact.fullName || "Unknown",
          email: contact.email || "",
          subject: contact.subject || "",
          message: contact.message || "",
          phone: contact.phoneNumber || "",
          timestamp:
            contact.timestamp || contact.createdAt || new Date().toISOString(),
        }));

        // Sort by timestamp (newest first)
        const sortedForms = processedContactForms.sort(
          (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
        );

        setContactForms(sortedForms);
        setFilteredForms(sortedForms);
      } catch (error) {
        setError(error.message || "Failed to fetch contact forms");
        setContactForms([]);
        setFilteredForms([]);
      } finally {
        setLoading(false);
      }
    };

    fetchContactForms();
    const intervalId = setInterval(fetchContactForms, 30000);

    return () => clearInterval(intervalId);
  }, []);

  // Filter contact forms based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredForms(contactForms);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = contactForms.filter(
      (contact) =>
        contact.fullName.toLowerCase().includes(query) ||
        (contact.phone && contact.phone.includes(query))
    );

    setFilteredForms(filtered);
  }, [searchQuery, contactForms]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  const handleView = (id) => {
    navigate(`/admin/responses/contact-form-responses/${id}`);
  };

  const handleDelete = async (id) => {
    if (
      window.confirm(
        "Are you sure you want to delete this contact form submission?"
      )
    ) {
      try {
        await deleteData("/contact_form", id);

        setContactForms((prevContactForms) =>
          prevContactForms.filter((contact) => contact.id !== id)
        );
      } catch (error) {
        console.error("Error deleting contact form:", error);
        alert("Failed to delete contact form.");
      }
    }
  };

  const handleBack = () => {
    navigate("/admin/admin-dashboard");
  };

  // Format date for display
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (e) {
      return "Unknown date";
    }
  };

  if (loading) {
    return <div className="loading-container">Loading contact forms...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <p>Error: {error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <button onClick={handleBack} className="back-btn">
        ← Back to Dashboard
      </button>

      <h1 className="admin-title">Contact Form Submissions Admin Panel</h1>

      <div className="search-container">
        <input
          type="text"
          placeholder="Search by name or phone number"
          value={searchQuery}
          onChange={handleSearch}
          className="search-input"
        />
        {searchQuery && (
          <button onClick={clearSearch} className="clear-search-btn">
            Clear
          </button>
        )}
      </div>

      {filteredForms.length === 0 ? (
        <div className="no-data-container">
          {searchQuery ? (
            <p className="no-data">No results match your search.</p>
          ) : (
            <p className="no-data">No contact form submissions found.</p>
          )}
          <button
            onClick={() => window.location.reload()}
            className="retry-btn"
          >
            Reload Data
          </button>
        </div>
      ) : (
        <div className="consultations-grid">
          {filteredForms.map((contact) => (
            <div key={contact.id} className="consultation-card">
              <div className="card-header">
                <h3 className="client-name">{contact.fullName}</h3>
                <span className="submission-date">
                  Date:{" "}
                  {(() => {
                    const date = new Date(contact.timestamp);
                    const year = date.getFullYear();
                    const month = (date.getMonth() + 1)
                      .toString()
                      .padStart(2, "0");
                    const day = date.getDate().toString().padStart(2, "0");
                    return `${year}-${month}-${day}`;
                  })()}
                  <br />
                  Time:{" "}
                  {(() => {
                    const date = new Date(contact.timestamp);
                    let hour = date.getHours();
                    const minutes = date
                      .getMinutes()
                      .toString()
                      .padStart(2, "0");
                    const ampm = hour < 12 ? "AM" : "PM";
                    hour = hour % 12 || 12;
                    return `${hour
                      .toString()
                      .padStart(2, "0")}:${minutes} ${ampm}`;
                  })()}
                </span>
              </div>
              <div className="card-content">
                <p>Email: {contact.email}</p>
                {contact.phone && <p>Phone: {contact.phone}</p>}
                <p>Subject: {contact.subject}</p>
              </div>
              <div className="card-actions">
                <button
                  onClick={() => handleView(contact.id)}
                  className="view-btn"
                >
                  View Details
                </button>
                <button
                  onClick={() => handleDelete(contact.id)}
                  className="delete-btn"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContactFormPanel;
